import { useSuspenseQuery } from '@tanstack/react-query';
import { updateWorkflowStageTaskDefinitionOptions, InterventionTypeOptions, getFullInterventionDefinitionOptions } from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { ClipboardList } from 'lucide-react';
import { LibraryForm } from '../../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	title: Yup.string().label('Title').nullable().required(),
	category: Yup.string().label('Category').nullable().required(),
	menuLabel: Yup.string().label('Menu Label').nullable(),
	definitionText: Yup.string().label('Definition Text').nullable(),
	definitionTextTemplate: Yup.string().label('Definition Text Template').nullable(),
	interventionTypeId: Yup.number().label('Intervention Type').nullable(),
	oasisSectionLetter: Yup.string().label('OASIS Section Letter').nullable(),
	duplicateProcedurePriority: Yup.number().label('Duplicate Procedure Priority').nullable(),
	oasisCodes: Yup.string().label('OASIS Codes').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	workflowStages: Yup.string().label('Workflow Stages').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	metadata: Yup.string().label('Metadata').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	flags: Yup.string().label('Flags').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	definitionFormControls: Yup.string().label('Definition Form Controls').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	definitionFormFields: Yup.string().label('Definition Form Fields').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	documentationFormControls: Yup.string().label('Documentation Form Controls').nullable().test('valid-json', 'Must be valid JSON', jsonValidator),
	documentationFormFields: Yup.string().label('Documentation Form Fields').nullable().test('valid-json', 'Must be valid JSON', jsonValidator)
});

export function EditInterventionDefinitionForm() {
	const { id } = useParams<'id'>();
	const navigate = useNavigate();
	const options = getFullInterventionDefinitionOptions(id!);
	const { data: definition } = useSuspenseQuery(options);

	const initialValues = {
		workflowStageTaskDefinitionId: id,
		title: definition.title ?? '',
		menuLabel: definition.menuLabel ?? '',
		category: definition.category ?? '',
		definitionText: definition.definitionText ?? '',
		definitionTextTemplate: definition.definitionTextTemplate ?? '',
		interventionTypeId: definition.interventionTypeId ?? null,
		canBePerformedByPatient: definition.canBePerformedByPatient ?? false,
		canBePerformedByCaregiver: definition.canBePerformedByCaregiver ?? false,
		planOfCareLocatorItem: definition.planOfCareLocatorItem ?? false,
		restrictedToClinicalTypesId: definition.restrictedToClinicalTypes?.map(u => u.id) ?? [],
		workflowStages: JSON.stringify(definition.documentationWorkflows ?? [], null, 2),
		metadata: JSON.stringify(definition.metadata ?? {}, null, 2),
		flags: JSON.stringify(definition.flags ?? [], null, 2),
		definitionFormControls: JSON.stringify(definition.definitionFormControls ?? [], null, 2),
		definitionFormFields: JSON.stringify(definition.definitionFormFields ?? [], null, 2),
		documentationFormControls: JSON.stringify(definition.documentationFormControls ?? [], null, 2),
		documentationFormFields: JSON.stringify(definition.documentationFormFields ?? [], null, 2)
	};

	return (
		<LibraryForm
			initialValues={initialValues}
			saveMutation={{
				...updateWorkflowStageTaskDefinitionOptions,
				invalidateQueryKeys: [options.queryKey, getSearchQueryKey('workflow-stage-task-definitions')]
			}}
			onSaveTransform={values => ({
				...values,
				oasisCodes: safeParseJson(values.oasisCodes, []),
				workflowStages: safeParseJson(values.workflowStages, []),
				dependsOnTasks: safeParseJson(values.dependsOnTasks, []),
				metadata: safeParseJson(values.metadata, {}),
				flags: safeParseJson(values.flags, []),
				definitionFormControls: safeParseJson(values.definitionFormControls, []),
				definitionFormFields: safeParseJson(values.definitionFormFields, []),
				documentationFormControls: safeParseJson(values.documentationFormControls, []),
				documentationFormFields: safeParseJson(values.documentationFormFields, [])
			})}
			TitleIcon={ClipboardList}
			title={`Edit Task Definition: ${definition.title}`}
			description={`Modify the configuration for ${definition.title} (${definition.systemName})`}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to Task Definitions',
				errorAction: () => navigate('/settings/libraries/intervention-definitions')
			}}
		>
			<h3 className="text-lg font-semibold text-gray-900">Basic Info</h3>
			<div className="grid grid-cols-2 gap-6">
				<TextFormField name="title" className="col-span-2" />
				<TextFormField name="menuLabel" />
				<TextFormField name="category" />
				<TextAreaFormField name="definitionText" className="col-span-2" />
				<TextAreaFormField name="definitionTextTemplate" className="col-span-2" />
			</div>

			<h3 className="mt-8 text-lg font-semibold text-gray-900">Intervention Configuration</h3>
			<div className="grid grid-cols-2 gap-6">
				<SelectFormField name="interventionTypeId" options={InterventionTypeOptions} />
				<div />
				<CheckboxFormField name="canBePerformedByPatient" />
				<CheckboxFormField name="canBePerformedByCaregiver" />
				<CheckboxFormField name="planOfCareLocatorItem" />
			</div>

			<h3 className="mt-8 text-lg font-semibold text-gray-900">Access Control</h3>
			<div className="grid grid-cols-2 gap-6">
				<CheckboxFormField name="subscribable" />
				<CheckboxFormField name="restrictedToClinicians" />
			</div>

			<h3 className="mt-8 text-lg font-semibold text-gray-900">Task Dependencies & Metadata</h3>
			<div className="grid grid-cols-2 gap-6">
				<TextAreaFormField name="workflowStages" className="col-span-2" inputProps={{ placeholder: '["stage1", "stage2"]' }} />
				<TextFormField name="duplicateProcedurePriority" inputProps={{ type: 'number' }} />
				<div />
				<TextAreaFormField name="metadata" className="col-span-2" inputProps={{ placeholder: '{"key": "value"}' }} />
				<TextAreaFormField name="flags" className="col-span-2" inputProps={{ placeholder: '["flag1", "flag2"]' }} />
			</div>

			<h3 className="mt-8 text-lg font-semibold text-gray-900">Form Definitions</h3>
			<div className="grid grid-cols-1 gap-6">
				<TextAreaFormField name="definitionFormControls" inputProps={{ rows: 8, placeholder: '[{ "name": "...", "controlType": "..." }]' }} />
				<TextAreaFormField name="definitionFormFields" inputProps={{ rows: 8, placeholder: '[{ "name": "...", "dataType": "..." }]' }} />
				<TextAreaFormField
					name="documentationFormControls"
					inputProps={{ rows: 8, placeholder: '[{ "name": "...", "controlType": "..." }]' }}
				/>
				<TextAreaFormField name="documentationFormFields" inputProps={{ rows: 8, placeholder: '[{ "name": "...", "dataType": "..." }]' }} />
			</div>
		</LibraryForm>
	);
}

function safeParseJson<T>(value: string | null | undefined, fallback: T): T {
	if (!value) return fallback;
	try {
		return JSON.parse(value);
	} catch {
		return fallback;
	}
}

function jsonValidator(value: string | null | undefined): boolean {
	if (!value) return true;
	try {
		JSON.parse(value);
		return true;
	} catch {
		return false;
	}
}
